import { createApi } from '@reduxjs/toolkit/query/react';
import {
  TGetDBCVersionsQuery,
  TGetDBCVersionsResponse,
  TGetTierGearBuSlotQuery,
  TGetTierTalentsQuery,
  TGetTierTrinketsQuery,
  TTierGearBySlotResponse,
  TTierTalentsResponse,
  TTierTrinketsResponse
} from '../../../../../../shared/types/statisticsTypes';
import {
  TClaimedCharacterLoadoutsResponse,
  TLoadoutAggregateRequest,
  TLoadoutAggregateResponse,
  TLoadoutParseRequest,
  TLoadoutParseResponse,
  TLoadoutPopularityRequest,
  TLoadoutPopularityResponse,
  TLoadoutsDiffRequest,
  TLoadoutsDiffResponse,
  TLoadoutsSnapshotStatusRequest,
  TLoadoutsSnapshotStatusResponse
} from '../../../../../../shared/types/talentCompareTypes';
import { networkBaseQuery } from '../../services/api/utils';

// Send `loadoutTexts` as a single comma-separated query param instead of a bracket-indexed array
// (`loadoutTexts[0]=...`), which Hapi's query parser drops. Comma is safe: Blizzard talent export
// strings are base64 and never contain a comma. The backend validator splits it back into a string[].
const serializeLoadoutTexts = <T extends { loadoutTexts: string[] }>({
  loadoutTexts,
  ...rest
}: T): Omit<T, 'loadoutTexts'> & { loadoutTexts: string } => ({
  ...rest,
  loadoutTexts: loadoutTexts.join(',')
});

export const specStatisticsApi = createApi({
  reducerPath: 'specStatisticsApi',
  baseQuery: networkBaseQuery,
  endpoints: (builder) => ({
    getTierTalentsData: builder.query<TTierTalentsResponse, TGetTierTalentsQuery>({
      query: (params) => ({ url: '/api/statistics/tier/talents', params })
    }),
    getTierGearData: builder.query<TTierGearBySlotResponse, TGetTierGearBuSlotQuery>({
      query: (params) => ({ url: '/api/statistics/tier/gear', params })
    }),
    getTierTrinketsData: builder.query<TTierTrinketsResponse, TGetTierTrinketsQuery>({
      query: (params) => ({ url: '/api/statistics/tier/trinkets', params })
    }),
    loadoutsAggregate: builder.query<TLoadoutAggregateResponse, TLoadoutAggregateRequest>({
      query: (params) => ({ url: '/api/spec-loadout/aggregate', params })
    }),
    loadoutsPopularity: builder.query<TLoadoutPopularityResponse, TLoadoutPopularityRequest>({
      query: (params) => ({ url: '/api/spec-loadout/popularity', params: serializeLoadoutTexts(params) })
    }),
    loadoutParse: builder.query<TLoadoutParseResponse, TLoadoutParseRequest>({
      query: (params) => ({ url: '/api/spec-loadout/parse', params })
    }),
    loadoutsDiff: builder.query<TLoadoutsDiffResponse, TLoadoutsDiffRequest>({
      query: (params) => ({ url: '/api/spec-loadout/diff', params: serializeLoadoutTexts(params) })
    }),
    loadoutsSnapshotStatus: builder.query<TLoadoutsSnapshotStatusResponse, TLoadoutsSnapshotStatusRequest>({
      query: (params) => ({ url: '/api/spec-loadout/snapshot-status', params: serializeLoadoutTexts(params) })
    }),
    getDBCVersions: builder.query<TGetDBCVersionsResponse, TGetDBCVersionsQuery | void>({
      query: (params) => ({ url: '/api/statistics/dbc-versions', params: params ?? {} }),
      keepUnusedDataFor: Number.POSITIVE_INFINITY
    }),
    claimedCharacterLoadouts: builder.query<TClaimedCharacterLoadoutsResponse, void>({
      query: () => ({ url: '/api/characters/claimed-loadouts' })
    })
  })
});

export const {
  useGetTierTalentsDataQuery,
  useGetTierGearDataQuery,
  useGetTierTrinketsDataQuery,
  useLoadoutsAggregateQuery,
  useLoadoutsPopularityQuery,
  useLoadoutParseQuery,
  useLoadoutsDiffQuery,
  useLoadoutsSnapshotStatusQuery,
  useGetDBCVersionsQuery,
  useClaimedCharacterLoadoutsQuery
} = specStatisticsApi;
